import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api, authStore, saasApi, saasAuth } from './lib/api';
import Layout from './components/Layout';
import Shell from './components/Shell';
import HomePage from './pages/HomePage';
import DownloadsPage from './pages/DownloadsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import Plans from './pages/Plans';
import InitialPlanConfirmation from './pages/InitialPlanConfirmation';
import Dashboard from './pages/Dashboard';
import DownloadApp from './pages/DownloadApp';
import LabelFormats from './pages/LabelFormats';
import Models from './pages/Models';
import Templates from './pages/Templates';
import LabelEditor from './pages/LabelEditor';
import ImageLibrary from './pages/ImageLibrary';
import Print from './pages/Print';
import Users from './pages/Users';
import Categories from './pages/Categories';
import Permissions from './pages/Permissions';
import DataSources from './pages/DataSources';
import Formulas from './pages/Formulas';
import Counters from './pages/Counters';
import History from './pages/History';
import Printers from './pages/Printers';
import ApiDocs from './pages/ApiDocs';
import Settings from './pages/Settings';
import Help from './pages/Help';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTenants from './pages/admin/AdminTenants';
import AdminTenantDetail from './pages/admin/AdminTenantDetail';
import AdminPlans from './pages/admin/AdminPlans';
import AdminPayments from './pages/admin/AdminPayments';
import AdminModels from './pages/admin/AdminModels';

function titleForPath(pathname) {
  if (pathname === '/login') return 'Entrar no Studio - EtiquetandoApp';
  if (pathname === '/planos') return 'Escolher Plano - EtiquetandoApp';
  if (pathname === '/onboarding') return 'Criar Conta - EtiquetandoApp';
  if (pathname === '/assinatura-inicial') return 'Confirmar Plano - EtiquetandoApp';
  if (pathname === '/downloads') return 'Downloads - EtiquetandoApp';
  if (pathname === '/biblioteca-imagens') return 'Biblioteca de Imagens - EtiquetandoApp';
  if (pathname === '/privacy-policy') return 'Política de Privacidade - EtiquetandoApp';
  if (pathname.startsWith('/admin')) return 'Admin SaaS - EtiquetandoApp';
  return 'EtiquetandoApp - Solução em Etiquetas';
}

function RouteEffects() {
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = titleForPath(pathname);
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function SiteLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <Layout pathname={pathname} onNavigate={navigate}>
      <div key={pathname}>
        <Outlet />
      </div>
    </Layout>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authStore.access) {
      setLoading(false);
      return;
    }
    api
      .me()
      .then(setUser)
      .catch(() => authStore.clear())
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (saasAuth.access) {
      saasApi.me().catch(() => saasAuth.clear());
    }
  }, []);

  async function handleLogout() {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    authStore.clear();
    setUser(null);
    navigate('/login');
  }

  return (
    <>
      <RouteEffects />
      {loading ? (
        <div className="grid h-screen place-items-center">Carregando…</div>
      ) : (
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/downloads" element={<DownloadsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          </Route>

          <Route path="/studio" element={<Navigate to="/login" replace />} />

          <Route
            path="/planos"
            element={user ? <Navigate to="/dashboard" replace /> : <Plans />}
          />
          <Route
            path="/login"
            element={user ? <Navigate to="/dashboard" replace /> : <Login onSuccess={setUser} />}
          />
          <Route
            path="/onboarding"
            element={user ? <Navigate to="/dashboard" replace /> : <Onboarding onSuccess={setUser} />}
          />
          <Route
            path="/assinatura-inicial"
            element={user ? <InitialPlanConfirmation /> : <Navigate to="/login" replace />}
          />

          <Route
            element={
              user ? (
                <Shell user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/baixar-app" element={<DownloadApp />} />
            <Route path="/formatos" element={<LabelFormats user={user} />} />
            <Route path="/modelos" element={<Models user={user} />} />
            <Route path="/templates" element={<Templates user={user} />} />
            <Route path="/templates/:id/editar" element={<LabelEditor user={user} />} />
            <Route path="/biblioteca-imagens" element={<ImageLibrary user={user} />} />
            <Route path="/impressao" element={<Print user={user} />} />
            <Route path="/usuarios" element={<Users user={user} />} />
            <Route path="/categorias" element={<Categories user={user} />} />
            <Route path="/permissoes" element={<Permissions />} />
            <Route path="/fontes-de-dados" element={<DataSources />} />
            <Route path="/formulas" element={<Formulas />} />
            <Route path="/contadores" element={<Counters />} />
            <Route path="/historico" element={<History />} />
            <Route path="/impressoras" element={<Printers />} />
            <Route path="/dispositivos" element={<Printers />} />
            <Route path="/documentacao" element={<ApiDocs />} />
            <Route path="/configuracoes" element={<Settings user={user} />} />
            <Route path="/ajuda" element={<Help />} />
          </Route>

          <Route
            path="/admin/login"
            element={saasAuth.access ? <Navigate to="/admin" replace /> : <AdminLogin />}
          />
          <Route
            element={
              saasAuth.access ? (
                <AdminLayout />
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          >
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/tenants" element={<AdminTenants />} />
            <Route path="/admin/modelos" element={<AdminModels />} />
            <Route path="/admin/tenants/:id" element={<AdminTenantDetail />} />
            <Route path="/admin/planos" element={<AdminPlans />} />
            <Route path="/admin/pagamentos" element={<AdminPayments />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </>
  );
}
